import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { organizations, leagues, seasons, getCurrentOrganization, setCurrentOrganization } from '../lib/api'

export default function SetupWizard({ onComplete, onDismiss }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Step 1: Organization
  const [orgData, setOrgData] = useState({ name: '', description: '' })
  const [createdOrg, setCreatedOrg] = useState(null)

  // Step 2: League
  const [leagueData, setLeagueData] = useState({ name: '', description: '' })
  const [createdLeague, setCreatedLeague] = useState(null)

  // Step 3: Season
  const [seasonData, setSeasonData] = useState({ name: '', start_date: '', end_date: '' })
  const [createdSeason, setCreatedSeason] = useState(null)

  // Check if user already has leagues
  const [hasExistingLeagues, setHasExistingLeagues] = useState(false)

  useEffect(() => {
    const checkExistingLeagues = async () => {
      try {
        const allLeagues = await leagues.getAll()
        setHasExistingLeagues(allLeagues && allLeagues.length > 0)
      } catch (err) {
        console.error('Error checking leagues:', err)
      }
    }
    checkExistingLeagues()
  }, [searchParams])

  const handleCreateOrg = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const org = await organizations.create(orgData)
      setCreatedOrg(org)
      setCurrentOrganization(org)
      setStep(2) // Go directly to league creation
    } catch (err) {
      setError('Failed to create organization: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateLeague = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const league = await leagues.create({
        ...leagueData,
        organization_id: createdOrg.id
      })
      setCreatedLeague(league)
      // Notify that leagues have changed
      window.dispatchEvent(new CustomEvent('leaguesChanged'))
      setStep(3) // Go to season step
    } catch (err) {
      setError('Failed to create league: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSeason = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const season = await seasons.create({
        ...seasonData,
        league_id: createdLeague.id,
        is_active: true
      })
      setCreatedSeason(season)
      // Navigate directly to league using slug if available
      const path = createdLeague.slug ? `/${createdLeague.slug}` : `/leagues/${createdLeague.id}`
      navigate(path)
      if (onComplete) onComplete()
    } catch (err) {
      setError('Failed to create season: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const getStepTitle = () => {
    switch(step) {
      case 1: return 'Step 1: Create Your Organization'
      case 2: return 'Step 2: Create Your First League'
      case 3: return 'Step 3: Create Your First Season'
      default: return ''
    }
  }

  const getStepDescription = () => {
    switch(step) {
      case 1: return 'Your organization is the top-level container for all your hockey operations.'
      case 2: return 'A league is a type of competition (e.g., "Adult A League", "Youth Development").'
      case 3: return 'A season is a specific time period when games are played (e.g., "Spring 2025").'
      default: return ''
    }
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="setup-wizard-title">
      <div className="modal-container max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 id="setup-wizard-title" className="modal-header mb-0">Getting Started with IceOps</h2>
            <button onClick={onDismiss} className="btn-icon">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mb-4">
            <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-ice-600' : 'bg-slate-200'}`} />
            <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-ice-600' : 'bg-slate-200'}`} />
            <div className={`flex-1 h-2 rounded-full ${step >= 3 ? 'bg-ice-600' : 'bg-slate-200'}`} />
          </div>

          <h3 className="text-xl font-semibold text-slate-900 mb-1">{getStepTitle()}</h3>
          <p className="text-sm text-slate-600">{getStepDescription()}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded border border-red-400">
            {error}
          </div>
        )}

        {/* Step 1: Organization */}
        {step === 1 && (
          <form onSubmit={handleCreateOrg} className="space-y-4">
            {/* Trial Info Banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-emerald-800">14-Day Free Trial</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    No credit card required. Full access to all features during your trial.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-ice-50 border border-ice-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-700 mb-2">
                <strong>What's an Organization?</strong>
              </p>
              <p className="text-xs text-slate-600">
                Think of it as your hockey association or club. You can run multiple leagues under one organization
                (e.g., "Adult A League", "Adult B League", "Youth League").
              </p>
            </div>

            <div>
              <label className="label">Organization Name *</label>
              <input
                type="text"
                value={orgData.name}
                onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                className="input w-full"
                placeholder="e.g., Chicago Hockey Association"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="label">Description (optional)</label>
              <textarea
                value={orgData.description}
                onChange={(e) => setOrgData({ ...orgData, description: e.target.value })}
                className="input w-full"
                rows="3"
                placeholder="Brief description of your organization"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Creating...' : 'Continue'}
              </button>
              <button type="button" onClick={onDismiss} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Step 2: League */}
        {step === 2 && (
          <form onSubmit={handleCreateLeague} className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-semibold text-emerald-700">
                  Organization Created: {createdOrg?.name}
                </p>
              </div>
            </div>

            <div className="bg-ice-50 border border-ice-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-slate-600">
                Now create your first league. You can add more leagues later for different skill levels or age groups.
              </p>
            </div>

            <div>
              <label className="label">League Name *</label>
              <input
                type="text"
                value={leagueData.name}
                onChange={(e) => setLeagueData({ ...leagueData, name: e.target.value })}
                className="input w-full"
                placeholder="e.g., Adult A League"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="label">Description (optional)</label>
              <textarea
                value={leagueData.description}
                onChange={(e) => setLeagueData({ ...leagueData, description: e.target.value })}
                className="input w-full"
                rows="3"
                placeholder="Brief description of this league"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setStep(1)} className="btn-secondary">
                Back
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Creating...' : 'Continue'}
              </button>
              <button type="button" onClick={onDismiss} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Season */}
        {step === 3 && (
          <form onSubmit={handleCreateSeason} className="space-y-4">
            <div className="bg-ice-50 border border-ice-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-700 mb-1">
                <strong>Organization:</strong> {createdOrg?.name}
              </p>
              <p className="text-sm text-slate-700 mb-2">
                <strong>League:</strong> {createdLeague?.name}
              </p>
              <p className="text-xs text-slate-600">
                Finally, create your first season. Seasons help you track games and standings over time.
              </p>
            </div>

            <div>
              <label className="label">Season Name *</label>
              <input
                type="text"
                value={seasonData.name}
                onChange={(e) => setSeasonData({ ...seasonData, name: e.target.value })}
                className="input w-full"
                placeholder="e.g., Spring 2025"
                required
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Start Date *</label>
                <input
                  type="date"
                  value={seasonData.start_date}
                  onChange={(e) => setSeasonData({ ...seasonData, start_date: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="label">End Date *</label>
                <input
                  type="date"
                  value={seasonData.end_date}
                  onChange={(e) => setSeasonData({ ...seasonData, end_date: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setStep(2)} className="btn-secondary">
                Back
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Creating...' : 'Start My Free Trial'}
              </button>
              <button type="button" onClick={onDismiss} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
